import type { FocusEvent, PropsWithChildren, RefObject } from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle } from 'react';
import type { BoxProps } from '../Core';
import { DropdownListContextProvider, useDropdownListContext } from './DropdownListContext';
import { DropdownListWrapper } from './styles';

interface DropdownListInnerProps extends BoxProps {
  /** When true, skip auto-focus on mount (submenu lists receive focus via Arrow Right) */
  isSubmenu?: boolean;
}

const DropdownListInner = forwardRef<HTMLDivElement, PropsWithChildren<DropdownListInnerProps>>(
  function DropdownListInner({ children, isSubmenu, ...props }, ref) {
    const { isActive, listRef, activeId, onKeyDown, highlightFirst } = useDropdownListContext();

    useImperativeHandle(ref, () => listRef.current!, [listRef]);

    useEffect(() => {
      if (isActive && !isSubmenu) {
        listRef.current?.focus();
      }
    }, [isActive, isSubmenu, listRef]);

    const handleFocus = useCallback(
      (e: FocusEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && activeId === undefined) {
          highlightFirst();
        }
      },
      [activeId, highlightFirst]
    );

    if (!isActive) {
      return <DropdownListWrapper {...props}>{children}</DropdownListWrapper>;
    }

    return (
      <DropdownListWrapper
        ref={listRef as RefObject<HTMLDivElement>}
        tabIndex={0}
        role="menu"
        aria-activedescendant={activeId}
        onKeyDown={onKeyDown}
        onFocus={handleFocus}
        {...props}
      >
        {children}
      </DropdownListWrapper>
    );
  }
);

export interface DropdownListProps extends BoxProps {
  onArrowLeftToParent?: () => void;
}

/**
 * Keyboard-navigable dropdown container for e.g. DropdownItem, DropdownHeader, and Divider.
 * Each DropdownList creates its own navigation context. When nested inside a DropdownItem
 * submenu, pass onArrowLeftToParent so Arrow Left returns focus to the parent list.
 */
export const DropdownList = forwardRef<HTMLDivElement, PropsWithChildren<DropdownListProps>>(function DropdownList(
  { children, onArrowLeftToParent, ...props },
  ref
) {
  const isSubmenu = onArrowLeftToParent !== undefined;
  return (
    <DropdownListContextProvider onArrowLeftToParent={onArrowLeftToParent}>
      <DropdownListInner ref={ref} isSubmenu={isSubmenu} {...props}>
        {children}
      </DropdownListInner>
    </DropdownListContextProvider>
  );
});
